// /src/engine/ldrawConfig.ts

/**
 * Centralized LDraw path config.
 *
 * Lets you point Brickerator at a CDN / different origin without code edits.
 *
 * Optional Vite env vars:
 * - VITE_LDRAW_BASE: base URL/path for LDraw parts (default: BASE_URL + "ldraw/")
 * - VITE_LDRAW_SHADOW_BASE: base URL/path for LDCad shadow parts (default: BASE_URL + "ldraw-shadow/")
 * - VITE_LDRAW_INDEX_BASE: base URL/path for JSON indexes (default: VITE_LDRAW_BASE or BASE_URL + "ldraw/")
 */

function ensureTrailingSlash(s: string): string {
  return s.endsWith('/') ? s : `${s}/`;
}

function isAbsoluteUrl(s: string): boolean {
  // Supports https://cdn..., http://..., and protocol-relative //cdn...
  return /^(https?:)?\/\//i.test(s);
}

function joinUnderBaseUrl(relativePath: string): string {
  const base = ensureTrailingSlash(import.meta.env.BASE_URL ?? '/');
  const rel = relativePath
    .trim()
    .replace(/^\.\//, '')   // "./ldraw/" -> "ldraw/"
    .replace(/^\/+/, '');   // "/ldraw/"  -> "ldraw/" (so we don't double-prefix)
  return ensureTrailingSlash(`${base}${rel}`);
}

/**
 * Normalize an LDraw base path.
 *
 * Supports:
 * - Absolute URLs: "https://cdn.example.com/ldraw/"
 * - Origin-absolute paths: "/ldraw/" (intentionally ignores Vite BASE_URL)
 * - Relative paths: "ldraw/" or "./ldraw/" (resolved under Vite BASE_URL)
 */
export function normalizeBasePath(raw: unknown, fallbackRelative: string): string {
  const v = typeof raw === 'string' ? raw.trim() : '';
  const input = ensureTrailingSlash(v || fallbackRelative);

  if (isAbsoluteUrl(input)) return input;
  if (input.startsWith('/')) return input; // origin-root on purpose
  return joinUnderBaseUrl(input);
}

// Defaults are RELATIVE so they automatically follow Vite's BASE_URL.
export const LDRAW_BASE = normalizeBasePath(import.meta.env.VITE_LDRAW_BASE, 'ldraw/');
export const LDRAW_SHADOW_BASE = normalizeBasePath(import.meta.env.VITE_LDRAW_SHADOW_BASE, 'ldraw-shadow/');
export const LDRAW_INDEX_BASE = normalizeBasePath(
  import.meta.env.VITE_LDRAW_INDEX_BASE ?? import.meta.env.VITE_LDRAW_BASE,
  'ldraw/'
);
